import { FC } from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { ReactComponent as CheckMarkIcon } from '../../assets/icons/checkmark.svg';

import { formatDuration } from '../../utils/FormatDuration';

const Container = styled.div(({ theme }) => {
  const { color, type } = theme.new;

  return `
    min-width: 19.375rem;
    background-color: ${color.neutral[0]};
    font-family: ${type.face};
    border-radius: 6px;
    border: 1px solid ${color.neutral[3]};
  `;
});

const Header = styled.div(({ theme }) => {
  const { color, type } = theme.new;

  return `
    padding: 0.125rem 0.0625rem 0.125rem 0.5625rem;
    background-color: ${color.neutral[1]};
    color: ${color.shade[2]};
    font-weight: ${type.weight.regular};
    letter-spacing: 0.0025rem;
    font-size: ${type.size[16]};
    line-height: 1.44em;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 0.375rem;
    border-bottom: 1px solid ${color.neutral[4]};
    border-radius: 6px 6px 0 0;
  `;
});

type DotProps = {
  theme: DefaultTheme;
  programId: number;
};

const Dot = styled.span<DotProps>(({ theme, programId }) => {
  return `
    display: block;
    border-radius: 100px;
    height: 0.5rem;
    width: 0.5rem;
    background-color: ${theme.new.color.program[programId]};
  `;
});

const HeaderTitle = styled.h4(({ theme }) => {
  const { color, type } = theme.new;

  return `
    font-size: ${type.size[16]};
    color: ${color.shade[2]};
    letter-spacing: 0.0025rem;
    line-height: 1.44em;
    font-weight: ${type.weight.regular};
    flex-grow: 1;
  `;
});

const HeaderTime = styled.div(({ theme }) => {
  const { color, type } = theme.new;

  return `
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
    padding: 0.5625rem 0.75rem 0.5625rem 0.5625rem;
    background-color: ${color.shade[1]};
    border-radius: 0 6px 0 0;
    color: ${color.neutral[7]};
    letter-spacing: 0.015rem;
    font-size: ${type.size[12]};
    font-weight: ${type.weight.bold};

    &.complete {
      color: ${color.shade[1]};
      background-color: ${color.brand[2].dark};
    }

    svg {
      height: 0.875rem;
      width: 0.875rem;

      path {
        fill: ${color.shade[1]};
      }
    }
  `;
});

const Body = styled.div``;

const BodyTitle = styled.h5(({ theme }) => {
  const { color, type } = theme.new;

  return `
    padding: 0.5rem;
    font-size: ${type.size[12]};
    font-weight: ${type.weight.bold};
    color: ${color.neutral[8]};
    letter-spacing: 0.015rem;
  `;
});

const ActivityList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ActivityItem = styled.ul(({ theme }) => {
  const { color } = theme.new;

  return `
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    padding: 0.59375rem 0.75rem 0.59375rem 0.5rem;

    &:not(:last-child) {
      border-bottom: 1px solid ${color.neutral[2]};
    }
  `;
});

const ActivityTitle = styled.div(({ theme }) => {
  const { color, type } = theme.new;

  return `
    font-size: ${type.size[14]};
    color: ${color.neutral[8]};
  `;
});

const ActivityTime = styled.div(({ theme }) => {
  const { color, type } = theme.new;

  return `
    color: ${color.neutral[8]};
    font-size: ${type.size[12]};
    letter-spacing: 0.015rem;
    font-weight: ${type.weight.bold};
  `;
});

type Activity = {
  title: string;
  duration: number;
};

interface ActivityCardProps {
  title: string;
  currentTime: number;
  maxTime: number;
  activities: Activity[];
  programId: number;
}

const ActivityCard: FC<ActivityCardProps> = ({ title, currentTime, maxTime, activities, programId }) => {
  const complete = currentTime / 60 >= maxTime;

  const activityLoop = activities.map((activity) => {
    return (
      <ActivityItem>
        <ActivityTitle>{activity.title}</ActivityTitle>

        <ActivityTime>{formatDuration(activity.duration, 'MM:SS')} min</ActivityTime>
      </ActivityItem>
    );
  });

  return (
    <Container>
      <Header>
        <Dot programId={programId} />
        <HeaderTitle>{title}</HeaderTitle>

        <HeaderTime className={complete ? 'complete' : ''}>
          {formatDuration(currentTime, 'MM:SS')} min / {formatDuration(maxTime * 60, 'MM:SS')} min{' '}
          {complete && <CheckMarkIcon />}
        </HeaderTime>
      </Header>

      <Body>
        <BodyTitle>Activities:</BodyTitle>
        <ActivityList>{activityLoop}</ActivityList>
      </Body>
    </Container>
  );
};

export default ActivityCard;
